// 游戏主界面
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../app/hooks';
import { selectUserInfo, selectAccount, selectBalance } from '../../data/accountSlice';
import { getImageUrl } from '../../utils/assets';
import { useWalletConnect } from '../../hooks/useWalletConnect';
import * as loader from "@assemblyscript/loader";
import DailyAnimation from './DailyAnimation';
import ChanceModal from './ChanceModal';
import WanxiangyiModal from './WanxiangyiModal';
import QiankundaiModal from './QiankundaiModal';
import MeetingModal from './MeetingModal';
import JusticeModal from './JusticeModal';
import GongdaozhiModal from './GongdaozhiModal';
import CelebrationModal from './CelebrationModal';
import TempleModal from './TempleModal';
import HelpSettingsModal from './HelpSettingsModal';
import SuccessGoodAnimation from './SuccessGoodAnimation';
import AgreementModal from '../../components/AgreementModal'; 
import './GameplayScreen.css';

import { checkIn, getCheckInStatus } from '../../api/user';
import { startGame, claimReward, StartGameResponse } from '../../api/game';
import { setUserProfile, setBalance } from '../../data/accountSlice';
import { useAppDispatch } from '../../app/hooks';
import toast from 'react-hot-toast';

const GameplayScreen: React.FC = () => {
  const { t } = useTranslation(['game']);
  const { connectWallet } = useWalletConnect();
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectAccount);
  const userInfo = useAppSelector(selectUserInfo);
  const balance = useAppSelector(selectBalance);
  
  const [showChanceModal, setShowChanceModal] = useState(false);
  const [showWanxiangyiModal, setShowWanxiangyiModal] = useState(false);
  const [showQiankundaiModal, setShowQiankundaiModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showJusticeModal, setShowJusticeModal] = useState(false);
  const [showGongdaozhiModal, setShowGongdaozhiModal] = useState(false);
  const [showCelebrationModal, setShowCelebrationModal] = useState(false);
  const [showTempleModal, setShowTempleModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [isPlayingDailyAnimation, setIsPlayingDailyAnimation] = useState(true);
  const [backgroundVideo, setBackgroundVideo] = useState<string | undefined>(undefined);
  const [isRingAnimationPaused, setIsRingAnimationPaused] = useState(false);
  const [successTrigger, setSuccessTrigger] = useState<number | undefined>(undefined);
  const [showSuccessGood, setShowSuccessGood] = useState(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const outerRingRef = React.useRef<HTMLDivElement>(null);

  // Wasm State
  const wasmRef = useRef<any>(null);
  const [wasmReady, setWasmReady] = useState(false);
  const [baseTime, setBaseTime] = useState(0);
  const [ticket, setTicket] = useState<StartGameResponse | null>(null);
  const [ticketFetchedAt, setTicketFetchedAt] = useState(0);
  const [lastSuccessTime, setLastSuccessTime] = useState(0);

  // Difficulty Configuration
  const getDifficultyConfig = () => {
    const level = parseInt(process.env.REACT_APP_GAME_DIFFICULTY || '3', 10);
    // Threshold <= 0.75 ensures the ring is inside the inner ring
    // Stricter thresholds (closer to min scale 0.657) make it harder
    const levels: Record<number, { threshold: number, duration: string }> = {
      1: { threshold: 0.75, duration: '6.0s' },
      2: { threshold: 0.75, duration: '5.2s' },
      3: { threshold: 0.75, duration: '4.5s' }, // Default
      4: { threshold: 0.72, duration: '3.8s' }, // Faster + Stricter
      5: { threshold: 0.68, duration: '3.2s' }, // Fastest + Strictest
    };
    return levels[level] || levels[3];
  };

  // Cooldown Configuration
  const getCooldownConfig = () => {
    // Default 30 seconds cooldown, configurable via env
    return parseInt(process.env.REACT_APP_GAME_COOLDOWN_SECONDS || '30', 10) * 1000;
  };

  const difficultyConfig = getDifficultyConfig();

  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;

  const formatAddress = (addr: string) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-6)}`;
  };

  // Check check-in status on load
  useEffect(() => {
    const fetchCheckInStatus = async () => {
      // 1. Check local cache first
      const today = new Date().toDateString(); // "Fri Jan 16 2026"
      const cachedDate = localStorage.getItem('last_checkin_date');
      const cachedStatus = localStorage.getItem('checkin_status');

      if (cachedDate === today && cachedStatus === 'true') {
        setIsCheckedIn(true);
        return;
      }

      // 2. If not cached or expired, fetch from API
      try {
        const status = await getCheckInStatus();
        setIsCheckedIn(status.isCheckedIn);
        
        // Update cache if checked in
        if (status.isCheckedIn) {
          localStorage.setItem('last_checkin_date', today);
          localStorage.setItem('checkin_status', 'true');
        }
      } catch (error) {
        console.error('Failed to get check-in status', error);
      }
    };
    fetchCheckInStatus();
  }, []);

  const handleCheckIn = async () => {
    // Check local cache first to avoid unnecessary API calls
    const today = new Date().toDateString();
    const cachedDate = localStorage.getItem('last_checkin_date');
    if (isCheckedIn || cachedDate === today) {
      toast(t('gameplay.alreadyCheckedIn'), { icon: '📅' });
      return;
    }

    try {
      const response = await checkIn();
      // API returns { success: true, isRepeated: boolean, ... }
      
      if (response.success) {
        setIsCheckedIn(true);
        
        // Update cache
        localStorage.setItem('last_checkin_date', today);
        localStorage.setItem('checkin_status', 'true');

        if (response.isRepeated) {
          toast(t('gameplay.alreadyCheckedIn'), { icon: '📅' });
        } else {
          toast.success(t('gameplay.checkinSuccess', { reward: response.reward }));
          // Update balance directly from response
          if (response.currentBalance) {
            dispatch(setBalance(response.currentBalance));
          }
        }
      } else {
        // Should be handled by catch block if API throws error, but safe to have else
        toast.error(t('gameplay.checkinFailed'));
      }
    } catch (error: any) {
      console.error('Check-in failed', error);
      toast.error(error.message || t('gameplay.checkinFailed'));
    }
  };

  // Initialize Wasm and Game Ticket
  useEffect(() => {
    // 1. Load Wasm
    fetch("game_core.wasm")
      .then(response => loader.instantiate(response, {
        env: {
          abort: () => console.log("Abort called from wasm")
        }
      }))
      .then(result => {
        wasmRef.current = result.exports;
        setWasmReady(true);
        setBaseTime(Date.now()); // Set baseline for animation sync
        console.log("Wasm loaded successfully");
      })
      .catch(err => console.error("Failed to load Wasm:", err));

    // 2. Fetch initial game ticket
    const fetchTicket = async () => {
      try {
        const data = await startGame();
        setTicket(data);
        setTicketFetchedAt(Date.now());
        console.log("Game ticket fetched:", data);
      } catch (error) {
        console.error("Failed to start game:", error);
        toast.error(t('gameplay.startGameFailed'));
      }
    };
    fetchTicket();
  }, []);

  const handleRingClick = async () => {
    // 1. Pause animations
    setIsRingAnimationPaused(true);
    
    // 2. Check condition using Wasm
    if (wasmReady && wasmRef.current && ticket) {
      const clickTime = Date.now();
      
      // Check ticket expiration (e.g. 110s)
      if (Date.now() - ticketFetchedAt > 110000) {
        console.log("Ticket expired, refreshing...");
        try {
          const newTicket = await startGame();
          setTicket(newTicket);
          setTicketFetchedAt(Date.now());
          // Retry with new ticket? Or just let user click again?
          // For simplicity, we just refresh and ask user to click again or process immediately if possible.
          // Here we might need to update ticket ref immediately to proceed, but state update is async.
          // So we return and let user click again or use ref for ticket.
          // Let's just return and show toast for now.
          //toast('Ticket refreshed, please try again.', { icon: '🔄' });
          setIsRingAnimationPaused(false);
          return;
        } catch (e) {
          console.error("Failed to refresh ticket", e);
          setIsRingAnimationPaused(false);
          return;
        }
      }

      const level = parseInt(process.env.REACT_APP_GAME_DIFFICULTY || '3', 10);
      
      const { verifyHit, __newString, __getString } = wasmRef.current;
      
      // Pass strings to Wasm memory
      const noncePtr = __newString(ticket.nonce);
      
      // Call Wasm verification
      // verifyHit(clickTime, startTime, noncePtr, difficulty)
      const tokenPtr = verifyHit(clickTime, baseTime, noncePtr, level);
      const token = __getString(tokenPtr);
      
      if (token) {
        console.log(`Wasm Success! Token: ${token}`);
        setSuccessTrigger(Date.now());
        setLastSuccessTime(Date.now()); // Update last success time
        setShowSuccessGood(true);
        
        // Send token to backend
        try {
          const rewardRes = await claimReward({
            gameId: ticket.gameId,
            token: token,
            clickTime: clickTime
          });
          
          toast.success(t('gameplay.rewardSuccess', { reward: rewardRes.reward }), {
            position: 'top-center',
          });
          
          // Update ticket for next round
          setTicket({
            gameId: rewardRes.nextGameId,
            nonce: rewardRes.nextNonce,
            serverTime: Date.now() // Mock server time or use response if available
          });
          setTicketFetchedAt(Date.now());
          
          // Optionally update balance if returned
          // if (rewardRes.currentBalance) dispatch(updateBalance(rewardRes.currentBalance));
          
        } catch (error: any) {
          console.error("Claim reward failed:", error);
          toast.error(error.message || t('gameplay.rewardFailed'));
        }
      } else {
        console.log('Wasm Missed!');
      }
    } else {
      // Fallback or loading state
      console.log("Wasm or Ticket not ready yet");
    }
    
    // 3. Resume animations after a short delay (e.g. 500ms to show the pause feedback)
    setTimeout(() => {
      setIsRingAnimationPaused(false);
    }, 500);
  };

  const handleWalletClick = () => {
    connectWallet();
  };

  // Determine day/night video
  useEffect(() => {
    const updateBackground = () => {
      const hour = new Date().getHours();
      // 7:00 - 19:00 is day
      if (hour >= 7 && hour < 19) {
        setBackgroundVideo(getImageUrl('gameplay/day.mp4'));
      } else {
        setBackgroundVideo(getImageUrl('gameplay/night.mp4'));
      }
    };

    updateBackground();
    const timer = setInterval(updateBackground, 60000); // Check every minute
    return () => clearInterval(timer);
  }, []);

  // Rem adaptation logic
  useEffect(() => {
    const setRem = () => {
      const html = document.documentElement;
      let width = html.clientWidth;
      // Limit max width for PC view to keep 750px design
      if (width > 750) {
        width = 750;
      }
      html.style.fontSize = width / 7.5 + 'px';
    };

    setRem();
    window.addEventListener('resize', setRem);
    
    return () => {
      window.removeEventListener('resize', setRem);
    };
  }, []);

  return (
    <div className="main-scroll-container">
      <div id="main-canvas" className="main-canvas">
        <div id="main-frame" className="main-frame">
          {/* 背景元素组 */}
          <video 
            className="main-background" 
            src={backgroundVideo} 
            autoPlay 
            loop 
            muted 
            playsInline
            style={{ objectFit: 'cover' }} 
          />
          <DailyAnimation 
            className="center-decoration" 
            isPlaying={isPlayingDailyAnimation}
            successTrigger={successTrigger}
            // If you want to show the original static image when animation is not playing (and not just the first frame),
            // you might need to adjust logic. For now, we assume stopping means showing first frame or just pausing.
            // But if we want to support 'center_frame.png' as fallback:
            // style={{ backgroundImage: !isPlayingDailyAnimation ? getImg('center_frame.png') : 'none' }}
          />
          <div className="top-right-icon" style={{ backgroundImage: getImg('top_right_icon.png') }} onClick={() => setShowHelpModal(true)} />
          <div className="side-decoration-left" style={{ backgroundImage: getImg('side_border.png') }} />
          <div className="side-decoration-right" style={{ backgroundImage: getImg('side_border.png') }} />
          
          {/* 顶部信息元素组 */}
          <div className="top-banner" style={{ backgroundImage: getImg('top_banner.png') }} />
          <div className="address-box" style={{ backgroundImage: getImg('address_box.png') }} />
          <span className="address-text" onClick={handleWalletClick} style={{ cursor: 'pointer' }}>
            {formatAddress(account || '')}
          </span>
          <div className="corner-icon" style={{ backgroundImage: getImg('corner_icon.png') }} onClick={() => setShowGongdaozhiModal(true)} />
          
          {/* 玩家信息元素组 */}
          <div className="avatar-level" style={{ backgroundImage: getImg('avatar_level.png'), cursor: 'pointer' }} onClick={handleWalletClick} />
          <span className="avatar-level-text">{userInfo?.level || 10}</span>
          <span className="player-name" onClick={() => setShowGongdaozhiModal(true)} style={{ cursor: 'pointer' }}>FV</span>
          <span className="player-level" onClick={() => setShowGongdaozhiModal(true)} style={{ cursor: 'pointer' }}>{parseInt((balance || '0').replace(/,/g, ''), 10)}</span>
          
          <div className="hp-bar-bg" style={{ backgroundImage: getImg('hp_bar_bg.png') }} />
          <div className="hp-bar-fill" style={{ backgroundImage: getImg('hp_bar_fill.png') }} />
          <div className="hp-bar-border" style={{ backgroundImage: getImg('hp_bar_border.png') }} />
          <span className="hp-label">HP</span>
          <span className="hp-value">{userInfo?.currentHp || 300}/{userInfo?.maxHp || 300}</span>
          
          {/* 功能按钮组 */}
          <div 
            className={`function-button btn-checkin ${isCheckedIn ? 'disabled' : ''}`} 
            style={{ backgroundImage: getImg('btn_checkin.png'), filter: isCheckedIn ? 'grayscale(100%)' : 'none' }} 
            onClick={handleCheckIn}
          />
          <span className="function-button-text btn-checkin-text">{t('gameplay.menu.checkin')}</span>
          
          <div className="function-button btn-meeting" style={{ backgroundImage: getImg('btn_meeting.png') }} onClick={() => setShowMeetingModal(true)} />
          <span className="function-button-text btn-meeting-text">{t('gameplay.menu.meeting')}</span>
          
          <div className="function-button btn-justice" style={{ backgroundImage: getImg('btn_justice.png') }} onClick={() => setShowJusticeModal(true)} />
          <span className="function-button-text btn-justice-text">{t('gameplay.menu.justice')}</span>
          
          <div className="function-button btn-chance" style={{ backgroundImage: getImg('btn_chance.png') }} onClick={() => setShowChanceModal(true)} />
          <span className="function-button-text btn-chance-text">{t('gameplay.menu.chance')}</span>
          
          <div className="function-button btn-costume" style={{ backgroundImage: getImg('btn_costume.png') }} onClick={() => setShowWanxiangyiModal(true)} />
          <span className="function-button-text btn-costume-text">{t('gameplay.menu.costume')}</span>
          
          <div className="function-button btn-treasure" style={{ backgroundImage: getImg('btn_treasure.png') }} onClick={() => setShowQiankundaiModal(true)} />
          <span className="function-button-text btn-treasure-text">{t('gameplay.menu.treasure')}</span>
          
          <div className="function-button btn-temple" style={{ backgroundImage: getImg('btn_temple.png') }} onClick={() => setShowTempleModal(true)} />
          <span className="function-button-text btn-temple-text">{t('gameplay.menu.temple')}</span>
          
          <div className="function-button btn-celebration" style={{ backgroundImage: getImg('btn_celebration.png') }} onClick={() => setShowCelebrationModal(true)} />
          <span className="function-button-text btn-celebration-text">{t('gameplay.menu.celebration')}</span>
          
          <div className="function-button btn-help" style={{ backgroundImage: getImg('btn_help.png') }} onClick={() => setShowAgreementModal(true)} />
          <span className="function-button-text btn-help-text">{t('gameplay.menu.help')}</span>
          
          {/* 底部装饰元素组 */}
          <div className="bottom-rings-hit-area" onClick={handleRingClick}>
            {showSuccessGood && (
              <SuccessGoodAnimation 
                isPlaying={true} 
                onAnimationEnd={() => setShowSuccessGood(false)} 
              />
            )}
          </div>
          <div 
            className="bottom-inner-ring-container"
            style={{
              animationPlayState: isRingAnimationPaused ? 'paused' : 'running'
            }}
          >
            <div 
              className={`bottom-inner-ring ${showSuccessGood ? 'recoil' : ''}`}
              style={{ 
                backgroundImage: getImg('bottom_inner_ring.png')
              }} 
            />
          </div>
          <div 
            className="bottom-middle-ring" 
            style={{ 
              backgroundImage: getImg('bottom_middle_ring.png'),
              animationPlayState: isRingAnimationPaused ? 'paused' : 'running'
            }} 
          />
          <div 
            ref={outerRingRef}
            className="bottom-outer-ring" 
            style={{ 
              backgroundImage: getImg('bottom_outer_ring.png'),
              animationPlayState: isRingAnimationPaused ? 'paused' : 'running',
              animationDuration: difficultyConfig.duration
            }} 
          />
          
          {/* 弹窗组件 */}
          <ChanceModal visible={showChanceModal} onClose={() => setShowChanceModal(false)} />
          <WanxiangyiModal visible={showWanxiangyiModal} onClose={() => setShowWanxiangyiModal(false)} />
          <QiankundaiModal visible={showQiankundaiModal} onClose={() => setShowQiankundaiModal(false)} />
          <MeetingModal visible={showMeetingModal} onClose={() => setShowMeetingModal(false)} />
          <JusticeModal visible={showJusticeModal} onClose={() => setShowJusticeModal(false)} />
          <GongdaozhiModal visible={showGongdaozhiModal} onClose={() => setShowGongdaozhiModal(false)} />
          <CelebrationModal visible={showCelebrationModal} onClose={() => setShowCelebrationModal(false)} />
          <TempleModal visible={showTempleModal} onClose={() => setShowTempleModal(false)} />
          <HelpSettingsModal visible={showHelpModal} onClose={() => setShowHelpModal(false)} />
          <AgreementModal visible={showAgreementModal} onClose={() => { setShowAgreementModal(false);}} />
        </div>
      </div>
    </div>
  );
};

export default GameplayScreen;
